import { ref, computed } from 'vue';
import { useStore } from 'vuex';
import { useToast } from 'primevue/usetoast';

export function useSynchronization() {
  const store = useStore();
  const toast = useToast();
  
  // État
  const isSyncing = computed(() => store.getters['sync/isSyncing']);
  const isOnline = computed(() => store.getters['sync/isOnline']);
  const isUserOfflineMode = computed(() => store.getters.isUserOfflineMode);
  const hasPendingUpdates = computed(() => store.getters['sync/hasPendingUpdates']);
  const hasManualSyncNeeded = ref(false);
  const pendingUpdatesCount = ref(0);

  // Fonction pour obtenir le texte du tooltip du bouton de synchronisation
  const getSyncTooltip = () => {
    if (isUserOfflineMode.value) return 'Mode hors ligne actif';
    if (!isOnline.value) return 'Mode hors ligne (pas de connexion)';
    if (isSyncing.value) return 'Synchronisation en cours...';
    if (hasManualSyncNeeded.value) return 'Erreurs de synchronisation - action requise';
    if (hasPendingUpdates.value) return `${pendingUpdatesCount.value} modifications à synchroniser`;
    return 'Toutes les données sont synchronisées';
  };

  // Fonction pour gérer le clic sur le bouton de synchronisation
  const handleSyncClick = async () => {
    if (isSyncing.value) return;
    
    if (!isOnline.value && !isUserOfflineMode.value) {
      toast.add({
        severity: 'warn',
        summary: 'Mode hors ligne',
        detail: 'La synchronisation n\'est pas disponible en mode hors ligne',
        life: 3000
      });
      return;
    }
    
    try {
      // Informer l'utilisateur que la synchronisation commence
      toast.add({
        severity: 'info',
        summary: 'Synchronisation',
        detail: 'Synchronisation des données en cours...',
        life: 3000
      });
      
      const result = await store.dispatch('sync/synchronize');
      
      // Mise à jour du compteur
      await updatePendingCount();
      
      if (result.manualSyncRequired) {
        toast.add({
          severity: 'error',
          summary: 'Synchronisation partielle',
          detail: 'Certaines mises à jour nécessitent une intervention manuelle. Cliquez sur le bouton de synchronisation pour plus d\'informations.',
          life: 5000
        });
      } else if (hasPendingUpdates.value) {
        toast.add({
          severity: 'info',
          summary: 'Synchronisation en cours',
          detail: `${pendingUpdatesCount.value} mises à jour en attente de synchronisation`,
          life: 3000
        });
      } else {
        toast.add({
          severity: 'success',
          summary: 'Synchronisation',
          detail: 'Toutes les données sont synchronisées',
          life: 3000
        });
      }
      
      return { success: true, needsReload: !hasPendingUpdates.value };
      
    } catch (error) {
      console.error('Erreur de synchronisation:', error);
      toast.add({
        severity: 'error',
        summary: 'Erreur',
        detail: 'Échec de la synchronisation: ' + (error.message || 'Erreur inconnue'),
        life: 3000
      });
      
      return { success: false };
    }
  };

  // Mise à jour du compteur de modifications en attente
  const updatePendingCount = async () => {
    try {
      const syncService = (await import('@/services/syncService')).syncService;
      const pendingData = await syncService.exportPendingData();
      pendingUpdatesCount.value = pendingData.updates.length + pendingData.images.length;
      
      // Vérifier s'il y a des mises à jour nécessitant une intervention manuelle
      hasManualSyncNeeded.value = await store.getters['sync/hasManualSyncItems']();
      
      return pendingUpdatesCount.value;
    } catch (error) {
      console.error('Erreur lors de la récupération du nombre de mises à jour en attente:', error);
      pendingUpdatesCount.value = 0;
      return 0;
    }
  };

  // Afficher les détails des erreurs de synchronisation
  const showSyncErrorDetails = () => {
    // Récupérer les statistiques d'erreur de synchronisation
    const errorDetails = store.getters['sync/getSyncErrorDetails'];
    const syncStats = store.getters['sync/getSyncStats'];
    
    let errorMessage = 'Certaines mises à jour requièrent votre attention';
    
    // Détails des erreurs
    if (Object.keys(errorDetails).length > 0) {
      errorMessage += '\n\nDétail des erreurs:';
      for (const [code, count] of Object.entries(errorDetails)) {
        let errorType = 'Erreur inconnue';
        if (code === 'network') errorType = 'Erreur réseau';
        else if (code.startsWith('status_')) {
          const status = code.replace('status_', '');
          errorType = `Erreur serveur (${status})`;
        }
        errorMessage += `\n- ${errorType}: ${count} occurrence(s)`;
      }
    }
    
    errorMessage += '\n\nLes données seront conservées localement. Veuillez réessayer ultérieurement ou contacter le support technique si le problème persiste.';
    
    toast.add({
      severity: 'error',
      summary: 'Problèmes de synchronisation',
      detail: errorMessage,
      life: 10000,
      closable: true
    });
  };

  return {
    // État
    isSyncing,
    isOnline,
    isUserOfflineMode,
    hasPendingUpdates,
    hasManualSyncNeeded,
    pendingUpdatesCount,
    
    // Méthodes
    getSyncTooltip,
    handleSyncClick,
    updatePendingCount,
    showSyncErrorDetails
  };
} 